import {
  DiagnosticSeverity,
  type Diagnostic,
  type TextDocumentChangeEvent,
} from 'vscode-languageserver';
import type { TextDocument } from 'vscode-languageserver-textdocument';
import { URI, Utils as UriUtils } from 'vscode-uri';
import { Injectable, createInterfaceId } from '@gitlab-org/di';
import { ConfigService, IConfig, ISecurityScannerOptions } from './config_service';
import { DocumentService } from './document_service';
import { log } from './log';
import { TextDocumentChangeListenerType } from './text_document_change_listener_type';
import { ClientFeatureFlags, FeatureFlagService } from './feature_flags';
import { DiagnosticsPublisher, DiagnosticsPublisherFn } from './diagnostics_publisher';
import { handleFetchError } from './handle_fetch_error';

export const DEFAULT_SCANNER_SERVICE_URL = '';

interface Vulnerability {
  name: string;
  description: string;
  severity: string;
  location: { start_line: number; end_line: number };
}
export interface SecurityDiagnosticsPublisher extends DiagnosticsPublisher {}

export const SecurityDiagnosticsPublisher = createInterfaceId<SecurityDiagnosticsPublisher>(
  'SecurityDiagnosticsPublisher',
);

@Injectable(SecurityDiagnosticsPublisher, [FeatureFlagService, ConfigService, DocumentService])
export class DefaultSecurityDiagnosticsPublisher implements SecurityDiagnosticsPublisher {
  #publish: DiagnosticsPublisherFn | undefined;

  #opts?: ISecurityScannerOptions;

  #featureFlagService: FeatureFlagService;

  constructor(
    featureFlagService: FeatureFlagService,
    configService: ConfigService,
    documentService: DocumentService,
  ) {
    this.#featureFlagService = featureFlagService;

    configService.onConfigChange((config: IConfig) => {
      this.#opts = config.client.securityScannerOptions;
    });

    documentService.onDocumentChange(
      async (
        event: TextDocumentChangeEvent<TextDocument>,
        handlerType: TextDocumentChangeListenerType,
      ) => {
        if (handlerType === TextDocumentChangeListenerType.onDidSave) {
          await this.#runSecurityScan(event.document);
        }
      },
    );
  }

  init(callback: DiagnosticsPublisherFn): void {
    this.#publish = callback;
  }

  async #runSecurityScan(document: TextDocument) {
    try {
      if (!this.#publish) {
        throw new Error('The DefaultSecurityService has not been initialized. Call init first.');
      }
      if (!this.#featureFlagService.isClientFlagEnabled(ClientFeatureFlags.RemoteSecurityScans)) {
        return;
      }
      if (!this.#opts?.enabled) {
        return;
      }
      if (!this.#opts) {
        return;
      }
      const url = this.#opts.serviceUrl ?? DEFAULT_SCANNER_SERVICE_URL;
      if (url === '') {
        return;
      }

      const content = document.getText();
      const fileName = UriUtils.basename(URI.parse(document.uri));
      const formData = new FormData();
      const blob = new Blob([content]);
      formData.append('file', blob, fileName);

      const request = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      };

      log.debug(`Executing request to url="${url}" with contents of "${fileName}"`);

      const response = await fetch(url, request);

      await handleFetchError(response, 'security scan');
      const json = await response.json();
      const { vulnerabilities: vulns } = json;
      if (vulns == null || !Array.isArray(vulns)) {
        return;
      }

      const diagnostics: Diagnostic[] = vulns.map((vuln: Vulnerability) => {
        let message = `${vuln.name}\n\n${vuln.description.substring(0, 200)}`;
        if (vuln.description.length > 200) {
          message += '...';
        }
        const severity =
          vuln.severity === 'high' ? DiagnosticSeverity.Error : DiagnosticSeverity.Warning;

        // TODO: Use a more precise range when it's available from the service.
        return {
          message,
          range: {
            start: {
              line: vuln.location.start_line - 1,
              character: 0,
            },
            end: {
              line: vuln.location.end_line,
              character: 0,
            },
          },
          severity,
          source: 'gitlab_security_scan',
        };
      });

      await this.#publish({
        uri: document.uri,
        diagnostics,
      });
    } catch (error) {
      log.warn('SecurityScan: failed to run security scan', error);
    }
  }
}
